import React, { useState, useEffect } from 'react';
import { Row, Col, Button, Table, Form } from 'react-bootstrap';
import Select from 'react-select';
import { DetailedProduct, ProductVariant, CatalogueConfig } from '../../../../api/shop/types/productTypes';
import { useCatalogueConfig } from '../../../../contexts/CatalogueConfigContext';
import { FulfillmentBadge } from '../../../../Common/BadgeStyles';

interface ProductVariantsProps {
  data: Partial<DetailedProduct>;
  onChange: (updates: Partial<DetailedProduct>) => void;
  isReadOnly?: boolean;
  isSportsNutrition?: boolean;
}

const ProductVariants: React.FC<ProductVariantsProps> = ({ data, onChange, isReadOnly = false, isSportsNutrition = false }) => {
  // Use shared config from context instead of individual API call
  const { config, loading } = useCatalogueConfig();

  // Color mapping for display - fallback colors if not in config
  const colorMap: { [key: string]: string } = {
    'White': '#FFFFFF',
    'Black': '#000000',
    'Blue': '#0066CC',
    'Red': '#CC0000',
    'Green': '#00CC00',
    'Yellow': '#FFCC00',
    'Gray': '#808080',
    'Grey': '#808080',
    'Orange': '#FF6600',
    'Purple': '#800080',
    'Pink': '#FFC0CB',
    'Brown': '#A52A2A',
    'Silver': '#C0C0C0',
    'Gold': '#FFD700',
    'Bronze': '#CD7F32',
  };

  // Get color emoji/indicator for dropdown display
  const getColorIndicator = (colorName: string) => {
    const colorIndicators: { [key: string]: string } = {
      'White': '⚪',
      'Black': '⚫',
      'Blue': '🔵',
      'Red': '🔴',
      'Green': '🟢',
      'Yellow': '🟡',
      'Gray': '⚫',
      'Grey': '⚫',
      'Orange': '🟠',
      'Purple': '🟣',
      'Pink': '🩷',
      'Brown': '🟤',
      'Silver': '⚪',
      'Gold': '🟡',
      'Bronze': '🟤',
    };
    return colorIndicators[colorName] || '🔵';
  };

  // Convert config data to options
  const colorOptions = config?.colors.map(color => ({
    value: color.value,
    label: color.label,
    color: colorMap[color.value] || '#CCCCCC' // fallback color
  })) || [];

  // Get sizes based on selected category
  const getSizeOptions = () => {
    if (!config || !data.category) {
      return [];
    }
    
    const categoryKey = data.category;
    const sizesForCategory = config.sizes[categoryKey] || [];
    
    return sizesForCategory.map(size => ({
      value: size.value,
      label: size.label
    }));
  };

  const [sizeOptions, setSizeOptions] = useState<{value: string, label: string}[]>([]);

  // Update size options when config or category changes
  useEffect(() => {
    const newSizeOptions = getSizeOptions();
    setSizeOptions(newSizeOptions);
  }, [config, data.category]);



  // Get available colors for a specific variant (excluding already used color+size combinations)
  const getAvailableColorsForVariant = (currentIndex: number, currentSize: string) => {
    const existingVariants = data.variants || [];
    const usedCombinations = existingVariants
      .filter((_, index) => index !== currentIndex) // Exclude current variant
      .map(v => `${v.attributes.color}-${v.attributes.size}`);
    
    return colorOptions.filter(color => {
      if (!currentSize) return true; // If no size selected, show all colors
      const combination = `${color.value}-${currentSize}`;
      return !usedCombinations.includes(combination);
    });
  };

  // Get available sizes for a specific variant (excluding already used color+size combinations)
  const getAvailableSizesForVariant = (currentIndex: number, currentColor: string) => {
    const existingVariants = data.variants || [];
    const usedCombinations = existingVariants
      .filter((_, index) => index !== currentIndex) // Exclude current variant
      .map(v => `${v.attributes.color}-${v.attributes.size}`);
    
    return sizeOptions.filter(size => {
      if (!currentColor) return true; // If no color selected, show all sizes
      const combination = `${currentColor}-${size.value}`;
      return !usedCombinations.includes(combination);
    });
  };


  const generateSKU = (productName: string, size: string, color: string): string => {
    const prefix = productName.substring(0, 3).toUpperCase();
    const sizeCode = size.substring(0, 2).toUpperCase();
    const colorCode = color.substring(0, 2).toUpperCase();
    return `${prefix}-${sizeCode}-${colorCode}`;
  };


  const handleVariantUpdate = (index: number, field: string, value: any) => {
    if (isReadOnly) return;

    const updatedVariants = [...(data.variants || [])];
    if (field === 'stockQty') {
      updatedVariants[index] = {
        ...updatedVariants[index],
        [field]: parseInt(value) || 1, // Temporary fix: Backend requires > 0
      };
    } else {
      updatedVariants[index] = {
        ...updatedVariants[index],
        [field]: value,
      };
    }
    onChange({ variants: updatedVariants });
  };

  // New handlers for repeater interface
  const handleAddVariant = () => {
    if (isReadOnly) return;

    const newVariant: ProductVariant = {
      sku: generateSKU(data.name || 'PROD', '', ''),
      attributes: {
        size: '',
        color: ''
      },
      stockQty: 1, // Temporary fix: Backend requires > 0
      status: 'ACTIVE'
    };

    const updatedVariants = [...(data.variants || []), newVariant];
    onChange({ variants: updatedVariants });
  };

  const handleRemoveVariant = (index: number) => {
    if (isReadOnly) return;

    const updatedVariants = [...(data.variants || [])];
    const removedVariant = updatedVariants[index];
    updatedVariants.splice(index, 1);

    // Check if this was the last variant with this color
    const removedColor = removedVariant.attributes.color;
    const hasOtherVariantsWithSameColor = updatedVariants.some(
      variant => variant.attributes.color === removedColor
    );

    let updates: Partial<DetailedProduct> = { variants: updatedVariants };

    // If no other variants have this color, optionally clean up colorImages
    // (keeping colorImages even if no variants for easier re-adding)
    
    onChange(updates);
  };

  const handleVariantFieldUpdate = (index: number, field: string, value: any) => {
    if (isReadOnly) return;

    const updatedVariants = [...(data.variants || [])];
    const variant = { ...updatedVariants[index] };
    let updates: Partial<DetailedProduct> = {};

    if (field === 'color' || field === 'size') {
      const oldColor = variant.attributes.color;
      const newColor = field === 'color' ? value : variant.attributes.color;
      const newSize = field === 'size' ? value : variant.attributes.size;
      
      // Check if this combination already exists in other variants
      const isDuplicate = updatedVariants.some((v, i) => 
        i !== index && v.attributes.color === newColor && v.attributes.size === newSize && newColor && newSize
      );

      if (isDuplicate) {
        // Show alert or handle duplicate - for now just return without updating
        alert(`Variant with ${newColor} color and ${newSize} size already exists!`);
        return;
      }
      
      variant.attributes = {
        ...variant.attributes,
        [field]: value
      };
      
      // Regenerate SKU when color or size changes
      if (newColor && newSize) {
        variant.sku = generateSKU(
          data.name || 'PROD',
          newSize,
          newColor
        );
      }

      // Handle colorImages updates when color changes
      if (field === 'color' && value !== oldColor) {
        const newColorImages = { ...data.colorImages };
        
        // Initialize empty array for new color if it doesn't exist
        if (value && !newColorImages[value]) {
          newColorImages[value] = [];
        }
        
        updates.colorImages = newColorImages;
      }
    } else if (field === 'stockQty') {
      variant.stockQty = parseInt(value) || 0;
    } else if (field === 'status') {
      variant.status = value;
    }

    updatedVariants[index] = variant;
    updates.variants = updatedVariants;
    
    onChange(updates);
  };

  // Get unique colors and sizes from existing variants
  const existingColors = data.variants 
    ? Array.from(new Set(data.variants.map(v => v.attributes.color)))
    : [];
  
  const existingSizes = data.variants 
    ? Array.from(new Set(data.variants.map(v => v.attributes.size)))
    : [];

  // Badge component for colors
  const ColorBadge = ({ color }: { color: string }) => {
    // Find exact match in current color options - show exact config label
    const exactMatch = colorOptions.find(c => c.value === color);
    const colorLabel = exactMatch ? exactMatch.label : color;
    
    return (
      <div
        style={{
          backgroundColor: '#E6F1FF',
          color: '#006EFF',
          border: '1px solid #006EFF',
          fontFamily: 'Inter, Helvetica Neue, Arial, sans-serif',
          fontWeight: 400,
          fontSize: '12px',
          lineHeight: '20px',
          textAlign: 'center',
          textTransform: 'none',
          minWidth: '60px',
          height: '32px',
          paddingLeft: '12px',
          paddingRight: '12px',
          paddingTop: '6px',
          paddingBottom: '6px',
          borderRadius: '16px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          whiteSpace: 'nowrap',
          marginRight: '8px',
          marginBottom: '8px'
        } as React.CSSProperties}
      >
        {colorLabel}
      </div>
    );
  };

  // Badge component for sizes
  const SizeBadge = ({ size }: { size: string }) => {
    // Find exact match in current size options - show exact config label or nothing
    const exactMatch = sizeOptions.find(s => s.value === size);
    const sizeLabel = exactMatch ? exactMatch.label : size;
    
    return (
      <div
        style={{
          backgroundColor: '#E9FCF2',
          color: '#1B9E5C',
          border: '1px solid #1B9E5C',
          fontFamily: 'Inter, Helvetica Neue, Arial, sans-serif',
          fontWeight: 400,
          fontSize: '12px',
          lineHeight: '20px',
          textAlign: 'center',
          textTransform: 'none',
          minWidth: '50px',
          height: '32px',
          paddingLeft: '12px',
          paddingRight: '12px',
          paddingTop: '6px',
          paddingBottom: '6px',
          borderRadius: '16px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          whiteSpace: 'nowrap',
          marginRight: '8px',
          marginBottom: '8px'
        } as React.CSSProperties}
      >
        {sizeLabel}
      </div>
    );
  };

  // Show loading state while config is loading
  if (loading && !isReadOnly) {
    return (
      <div>
        <h4 className="product-detail-section-title mb-4">Customize Variants</h4>
        <div className="d-flex justify-content-center py-4">
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <span className="ms-2">Loading configuration...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h4 className="product-detail-section-title mb-4">Customize Variants</h4>
      
      {/* Show Colors and Sizes badges only in read-only mode */}
      {isReadOnly && (existingColors.length > 0 || existingSizes.length > 0) && (
        <Row className="mb-4">
          {/* Colors Column */}
          {existingColors.length > 0 && (
            <Col md={6}>
              <Form.Label className="product-detail-form-label" style={{ color: '#6C757D', fontSize: '14px', fontWeight: '400', marginBottom: '12px' }}>
                Selected Colors
              </Form.Label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {existingColors.map((color, index) => (
                  <ColorBadge key={index} color={color} />
          ))}
        </div>
            </Col>
          )}

          {/* Sizes Column */}
          {existingSizes.length > 0 && (
            <Col md={6}>
              <Form.Label className="product-detail-form-label" style={{ color: '#6C757D', fontSize: '14px', fontWeight: '400', marginBottom: '12px' }}>
                Selected Sizes
              </Form.Label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {existingSizes.map((size, index) => (
                  <SizeBadge key={index} size={size} />
          ))}
        </div>
            </Col>
          )}
        </Row>
      )}

      {/* Show variant repeater interface in edit mode */}
      {!isReadOnly && (
        <div className="mb-4">
          {!isSportsNutrition && (
            <div className="d-flex justify-content-between align-items-center mb-3">
              <Form.Label className="product-detail-form-label mb-0">
                Product Variants<span className="required-asterisk">*</span>
              </Form.Label>
            </div>
          )}

          {/* User-friendly variant repeater */}
          <div className="variant-cards-container">
            {(data.variants || []).map((variant, index) => (
              <div key={index} className="variant-card">
                <div className="variant-card-header">
                  <span className="variant-number">Variant {index + 1}</span>
                  <Button
                    variant="outline-danger"
                        size="sm"
                    onClick={() => handleRemoveVariant(index)}
                    title="Remove variant"
                    className="remove-variant-btn"
                  >
                    <i className="ri-close-line"></i>
                  </Button>
                </div>
                
                <Row className="g-3">
                  <Col xl={3} lg={6} md={6} sm={12}>
                    <Form.Group>
                      <Form.Label>
                        Color<span className="required-asterisk">*</span>
                      </Form.Label>
                      <Select
                        classNamePrefix="react-select"
                        value={
                          variant.attributes.color
                            ? {
                                value: variant.attributes.color,
                                label: colorOptions.find(c => c.value === variant.attributes.color)?.label || variant.attributes.color,
                                color: colorOptions.find(c => c.value === variant.attributes.color)?.color
                              }
                            : null
                        }
                        onChange={(selectedOption) => 
                          handleVariantFieldUpdate(index, 'color', selectedOption?.value || '')
                        }
                        options={getAvailableColorsForVariant(index, variant.attributes.size).map(color => ({
                          value: color.value,
                          label: color.label,
                          color: color.color
                        }))}
                        placeholder="Select Color"
                        formatOptionLabel={(option) => (
                          <div className="color-option">
                            <div 
                              className="color-dot-small" 
                              style={{ backgroundColor: option.color }}
                            />
                            <span>{option.label}</span>
                          </div>
                        )}
                        isSearchable={false}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          control: (base) => ({
                            ...base,
                            minHeight: '44px',
                            borderColor: '#ced4da'
                          })
                        }}
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col xl={3} lg={6} md={6} sm={12}>
                    <Form.Group>
                      <Form.Label>
                        Size<span className="required-asterisk">*</span>
                      </Form.Label>
                      <Select
                        classNamePrefix="react-select"
                        value={
                          variant.attributes.size
                            ? (() => {
                                // Find exact match in available sizes
                                const exactMatch = getAvailableSizesForVariant(index, variant.attributes.color)
                                  .find(s => s.value === variant.attributes.size);
                                return exactMatch 
                                  ? { value: exactMatch.value, label: exactMatch.label }
                                  : null; // Don't show invalid values
                              })()
                            : null
                        }
                        onChange={(selectedOption) => 
                          handleVariantFieldUpdate(index, 'size', selectedOption?.value || '')
                        }
                        options={getAvailableSizesForVariant(index, variant.attributes.color).map(size => ({
                          value: size.value,
                          label: size.label
                        }))}
                        placeholder="Select Size"
                        isSearchable={false}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          control: (base) => ({
                            ...base,
                            minHeight: '44px',
                            borderColor: '#ced4da'
                          })
                        }}
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col xl={3} lg={6} md={6} sm={12}>
                    <Form.Group>
                      <Form.Label>
                        Stock Quantity<span className="required-asterisk">*</span>
                      </Form.Label>
                      <div className="stock-quantity-wrapper">
                        <button
                          type="button"
                          className="stock-quantity-btn stock-quantity-increase"
                          onClick={() => handleVariantFieldUpdate(index, 'stockQty', Math.min(9999, (variant.stockQty || 0) + 1))}
                          disabled={variant.stockQty >= 9999}
                          title="Increase quantity"
                        >
                          <i className="ri-add-line"></i>
                        </button>
                        <Form.Control
                          type="number"
                          min="1"
                          max="9999"
                          value={variant.stockQty || 1}
                          onChange={(e) => {
                            const value = e.target.value;
                            const numericValue = value === '' ? 1 : Math.max(1, parseInt(value) || 1); // Temporary fix: Backend requires > 0
                            handleVariantFieldUpdate(index, 'stockQty', numericValue);
                          }}
                          placeholder="0"
                          className="stock-quantity-input"
                          style={{
                            minHeight: '44px',
                            textAlign: 'center',
                            fontWeight: '500',
                            flex: 1
                          }}
                        />
                        <button
                          type="button"
                          className="stock-quantity-btn stock-quantity-decrease"
                          onClick={() => handleVariantFieldUpdate(index, 'stockQty', Math.max(1, (variant.stockQty || 1) - 1))} // Temporary fix: Backend requires > 0
                          disabled={(variant.stockQty || 1) <= 1}
                          title="Decrease quantity"
                        >
                          <i className="ri-subtract-line"></i>
                        </button>
                      </div>
                    </Form.Group>
                  </Col>
                  
                  <Col xl={3} lg={6} md={6} sm={12}>
                    <Form.Group>
                      <Form.Label>
                        Status<span className="required-asterisk">*</span>
                      </Form.Label>
                      <Select
                        classNamePrefix="react-select"
                        value={{ value: variant.status, label: variant.status === 'ACTIVE' ? 'Active' : 'Inactive' }}
                        onChange={(selectedOption) => 
                          handleVariantFieldUpdate(index, 'status', selectedOption?.value || 'ACTIVE')
                        }
                        options={[
                          { value: 'ACTIVE', label: 'Active' },
                          { value: 'INACTIVE', label: 'Inactive' }
                        ]}
                        isSearchable={false}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          control: (base) => ({
                            ...base,
                            minHeight: '44px',
                            borderColor: '#ced4da'
                          })
                        }}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            ))}

            {/* Add New Variant Card — hidden for sports nutrition products */}
            {!isSportsNutrition && (
              <div className="variant-card add-variant-card">
                <div
                  className="add-variant-content"
                  onClick={handleAddVariant}
                  style={{
                    cursor: colorOptions.length === 0 || sizeOptions.length === 0 ? 'not-allowed' : 'pointer',
                    opacity: colorOptions.length === 0 || sizeOptions.length === 0 ? 0.5 : 1
                  }}
                >
                  <div className="add-variant-icon">
                    <i className="ri-add-line"></i>
                  </div>
                  <div className="add-variant-text">
                    <div className="add-variant-title">Add New Variant</div>
                    <div className="add-variant-subtitle">
                      {colorOptions.length === 0 ? "No colors available" :
                       sizeOptions.length === 0 ? "Select category first" :
                       "Create a color and size combination"}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Fulfillment Capabilities */}
      <div className="mb-4">
        {isReadOnly ? (
          <>
            <Form.Label className="product-detail-form-label" style={{ color: '#6C757D', fontSize: '14px', fontWeight: '400', marginBottom: '12px' }}>
              Fulfillment Capabilities
            </Form.Label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {(data.fulfillmentCapabilities || []).map((capability, index) => (
                <FulfillmentBadge key={index} capability={capability} />
              ))}
            </div>
          </>
        ) : (
          <>
            <Form.Label className="product-detail-form-label">
              Fulfillment Capabilities<span className="required-asterisk">*</span>
            </Form.Label>
        <div className="product-detail-checkbox-group">
          {([
            { value: 'PICKUP', label: 'Pickup' },
            { value: 'DELIVERY', label: 'Delivery' },
            { value: 'EXTERNAL_ADS', label: 'External Ads' },
          ] as const).map(({ value, label }) => {
            const isExternalAds = data.fulfillmentCapabilities?.includes('EXTERNAL_ADS');
            const isDisabled = isReadOnly || (value !== 'EXTERNAL_ADS' && !!isExternalAds);
            return (
              <div
                key={value}
                className={`product-detail-checkbox-item ${
                  data.fulfillmentCapabilities?.includes(value) ? 'selected' : ''
                } ${isDisabled && !isReadOnly ? 'disabled' : ''}`}
                onClick={() => {
                  if (isDisabled) return;
                  const current = data.fulfillmentCapabilities || [];
                  let updated;
                  if (value === 'EXTERNAL_ADS') {
                    updated = current.includes('EXTERNAL_ADS') ? [] : ['EXTERNAL_ADS' as const];
                  } else {
                    const withoutExternal = current.filter(c => c !== 'EXTERNAL_ADS');
                    updated = withoutExternal.includes(value)
                      ? withoutExternal.filter(c => c !== value)
                      : [...withoutExternal, value];
                  }
                  onChange({ fulfillmentCapabilities: updated });
                }}
                style={{ cursor: isDisabled ? 'not-allowed' : 'pointer', opacity: isDisabled && !isReadOnly ? 0.5 : 1 }}
              >
                <input
                  type="checkbox"
                  checked={data.fulfillmentCapabilities?.includes(value) || false}
                  onChange={() => {}}
                  disabled={isDisabled}
                />
                {label}
              </div>
            );
          })}
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProductVariants;